import { useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import type { DeliveryNote, DeliveryNoteRow } from "@shared/delivery-note";
import { AlertTriangle, Check, FileText, Loader2, Plus, RefreshCw, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { extractDeliveryNote } from "@/lib/delivery-note-api";
import { deliveryRowReady } from "@/lib/delivery-note-items";
import { parseGermanNumber } from "@/lib/formatters";

export interface DeliveryNoteImportProps {
  invoiceId: string;
  notes: DeliveryNote[];
  onChange: (notes: DeliveryNote[]) => void;
  onApply: (noteId: string) => void;
  onBusyChange?: (busy: boolean) => void;
}

const money = new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" });
const emptyRow = (): DeliveryNoteRow => ({
  id: `delivery-row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  articleNumber: "",
  material: "",
  quantity: null,
  unit: "",
  notes: "",
  unitPrice: null,
  selected: false,
  appliedItemId: null,
});

function nullableNumber(value: number | null, onChange: (next: number | null) => void, label: string, disabled: boolean) {
  return <NullableGermanNumberInput value={value} onChange={onChange} label={label} disabled={disabled} />;
}

function NullableGermanNumberInput({ value, onChange, label, disabled }: { value: number | null; onChange: (next: number | null) => void; label: string; disabled: boolean }) {
  const [text, setText] = useState(() => value === null ? "" : String(value).replace(".", ","));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value === null ? "" : new Intl.NumberFormat("de-AT", { maximumFractionDigits: 20 }).format(value));
  }, [value]);

  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      disabled={disabled}
      placeholder="—"
      value={text}
      onFocus={() => { focused.current = true; }}
      onChange={(event) => {
        const raw = event.target.value;
        const cleaned = raw.replace(/[^0-9.,\-]/g, "");
        setText(cleaned);
        if (!cleaned.trim()) {
          onChange(null);
          return;
        }
        const parsed = parseGermanNumber(cleaned);
        onChange(parsed !== null && Number.isFinite(parsed) && parsed >= 0 ? parsed : null);
      }}
      onBlur={() => {
        focused.current = false;
        if (!text.trim()) {
          onChange(null);
          setText("");
          return;
        }
        const parsed = parseGermanNumber(text);
        if (parsed === null || !Number.isFinite(parsed) || parsed < 0) {
          onChange(null);
          setText("");
          return;
        }
        const normalized = parsed;
        onChange(normalized);
        setText(new Intl.NumberFormat("de-AT", { maximumFractionDigits: 20 }).format(normalized));
      }}
      className="h-9 w-full min-w-20 rounded-md border border-input bg-background px-2 text-right text-sm tabular-nums outline-none transition focus-visible:ring-2 focus-visible:ring-ring"
    />
  );
}

export default function DeliveryNoteImport({
  invoiceId,
  notes,
  onChange,
  onApply,
  onBusyChange,
}: DeliveryNoteImportProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const latestNotesRef = useRef(notes);
  latestNotesRef.current = notes;
  const importingRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [retryFile, setRetryFile] = useState<File | null>(null);

  const updateNotes = (update: (current: DeliveryNote[]) => DeliveryNote[]) => {
    const next = update(latestNotesRef.current);
    latestNotesRef.current = next;
    onChange(next);
  };

  const importFile = async (file: File) => {
    if (importingRef.current) return;
    if (latestNotesRef.current.length >= 30) {
      setError("Eine Rechnung kann höchstens 30 Lieferscheine enthalten.");
      return;
    }
    importingRef.current = true;
    setBusy(true);
    setError("");
    setRetryFile(file);
    onBusyChange?.(true);
    try {
      const result = await extractDeliveryNote(invoiceId, file);
      const next = [...latestNotesRef.current, result];
      latestNotesRef.current = next;
      onChange(next);
      setRetryFile(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Der Lieferschein konnte nicht ausgewertet werden.");
    } finally {
      importingRef.current = false;
      setBusy(false);
      onBusyChange?.(false);
    }
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (file) void importFile(file);
  };

  const patchNote = (noteId: string, patch: Partial<DeliveryNote>) =>
    updateNotes((current) => current.map((note) => note.id === noteId ? { ...note, ...patch } : note));

  const patchRow = (noteId: string, rowId: string, patch: Partial<DeliveryNoteRow>) =>
    updateNotes((current) => current.map((note) => note.id !== noteId ? note : {
      ...note,
      rows: note.rows.map((row) => row.id === rowId && !row.appliedItemId ? { ...row, ...patch } : row),
    }));

  const addRow = (noteId: string) => patchNote(noteId, {
    rows: [...(latestNotesRef.current.find((note) => note.id === noteId)?.rows ?? []), emptyRow()],
  });

  const removeRow = (noteId: string, rowId: string) =>
    updateNotes((current) => current.map((note) => note.id !== noteId ? note : {
      ...note, rows: note.rows.filter((row) => row.id !== rowId || !!row.appliedItemId),
    }));

  return (
    <section aria-labelledby="delivery-note-heading" className="min-w-0 overflow-hidden rounded-xl border border-bauwerk-teal/20 bg-white shadow-sm">
      <header className="flex flex-col gap-4 border-b border-slate-200 bg-bauwerk-mint/30 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex items-start gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white text-bauwerk-teal shadow-sm">
            <FileText className="h-5 w-5" aria-hidden="true" />
          </span>
          <div>
            <h2 id="delivery-note-heading" className="text-base font-semibold text-bauwerk-teal">Lieferscheine</h2>
            <p className="mt-1 max-w-xl text-xs leading-relaxed text-slate-600">
              Positionen prüfen und manuell bepreisen, bevor sie in den Rechnungsentwurf übernommen werden.
            </p>
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,image/jpeg,image/png,image/webp,.pdf"
          className="sr-only"
          aria-label="PDF oder Lieferscheinbild auswählen"
          onChange={handleFile}
          disabled={busy}
        />
        <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} className="shrink-0">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="mr-2 h-4 w-4" aria-hidden="true" />}
          {busy ? "Lieferschein wird erkannt…" : "Lieferschein importieren"}
        </Button>
      </header>

      <div className="space-y-4 p-4 sm:p-5">
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-950">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <p>Die Originaldatei wird nur ausgewertet und nicht archiviert. Mengen und Einheiten bitte mit dem Lieferschein abgleichen. Einheitspreise immer selbst eintragen; es gibt keine Preisvorschläge.</p>
        </div>

        {busy && (
          <div role="status" aria-live="polite" className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="h-9 w-9 animate-pulse rounded-md bg-bauwerk-mint" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-3 w-44 animate-pulse rounded bg-slate-200" />
              <div className="h-2.5 w-3/4 animate-pulse rounded bg-slate-200" />
            </div>
            <span className="text-xs font-medium text-slate-600">Erkennung läuft</span>
          </div>
        )}

        {error && (
          <div role="alert" className="flex flex-col gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900 sm:flex-row sm:items-center sm:justify-between">
            <p>{error}</p>
            {retryFile && <Button type="button" variant="outline" size="sm" onClick={() => void importFile(retryFile)} disabled={busy}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" /> Erneut versuchen
            </Button>}
          </div>
        )}

        {!notes.length && !busy ? (
          <div className="rounded-lg border-2 border-dashed border-slate-200 px-4 py-9 text-center">
            <FileText className="mx-auto mb-2 h-7 w-7 text-slate-400" aria-hidden="true" />
            <p className="text-sm font-medium text-slate-700">Noch keine Lieferscheine importiert</p>
            <p className="mt-1 text-xs text-slate-500">PDF, JPEG, PNG oder WebP · bis 15 MB · Dateien einzeln nacheinander importieren</p>
          </div>
        ) : (
          <div className="space-y-5">
            {notes.map((note, noteIndex) => {
              const eligibleRows = note.rows.filter((row) => row.selected && !row.appliedItemId);
              const canApply = eligibleRows.length > 0 && eligibleRows.every(deliveryRowReady);
              const incompleteCount = eligibleRows.filter((row) => !deliveryRowReady(row)).length;
              return (
                <article key={note.id} className="overflow-hidden rounded-lg border border-slate-200">
                  <div className="grid grid-cols-1 gap-3 bg-slate-50/80 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end sm:p-4">
                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-slate-600">Lieferscheinnummer</span>
                      <Input value={note.deliveryNumber} onChange={(event) => patchNote(note.id, { deliveryNumber: event.target.value })} disabled={busy} placeholder="Optional" aria-label={`Lieferscheinnummer, ${note.fileName}`} />
                    </label>
                    <label className="space-y-1">
                      <span className="block text-xs font-medium text-slate-600">Lieferscheindatum</span>
                      <Input value={note.deliveryDate} onChange={(event) => patchNote(note.id, { deliveryDate: event.target.value })} disabled={busy} placeholder="Freitext, optional" aria-label={`Lieferscheindatum, ${note.fileName}`} />
                    </label>
                    <div className="flex min-w-0 items-center gap-2 sm:justify-end">
                      <span className="min-w-0 truncate text-xs text-slate-500" title={note.fileName}>{note.fileName}</span>
                      <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[11px] font-medium text-slate-600">{note.rows.length} Zeilen</span>
                    </div>
                  </div>

                  {note.warnings.length > 0 && (
                    <div className="space-y-1 border-t border-amber-200 bg-amber-50/70 px-3 py-3 sm:px-4">
                      <p className="flex items-center gap-2 text-xs font-semibold text-amber-950"><AlertTriangle className="h-4 w-4" aria-hidden="true" /> Erkannte Unklarheiten</p>
                      <ul className="list-inside list-disc space-y-1 text-xs leading-relaxed text-amber-900">
                        {note.warnings.map((warning, index) => <li key={`${index}-${warning}`}>{warning}</li>)}
                      </ul>
                    </div>
                  )}

                  <div className="border-t border-slate-200">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[1040px] border-collapse text-left text-xs">
                        <thead className="bg-white text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="w-10 px-3 py-3"><span className="sr-only">Auswahl</span></th>
                            <th className="w-32 px-2 py-3">Artikelnummer</th>
                            <th className="min-w-48 px-2 py-3">Material</th>
                            <th className="w-28 px-2 py-3 text-right">Menge</th>
                            <th className="w-24 px-2 py-3">Einheit</th>
                            <th className="min-w-40 px-2 py-3">Anmerkung</th>
                            <th className="w-32 px-2 py-3 text-right">EP netto EUR</th>
                            <th className="w-32 px-3 py-3 text-right">Zeilensumme</th>
                            <th className="w-12 px-2 py-3"><span className="sr-only">Zeile entfernen</span></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {note.rows.map((row, rowIndex) => {
                            const locked = busy || !!row.appliedItemId;
                            const ready = deliveryRowReady(row);
                            const rowTotal = row.quantity !== null && row.unitPrice !== null ? row.quantity * row.unitPrice : null;
                            return (
                              <tr key={row.id} className={`${row.appliedItemId ? "bg-emerald-50/60" : row.selected && !ready ? "bg-amber-50/50" : "bg-white"}`}>
                                <td className="px-3 py-2.5 align-top">
                                  {row.appliedItemId ? <span className="grid h-8 w-8 place-items-center rounded-full bg-emerald-100 text-emerald-800" title="Bereits in die Rechnung übernommen"><Check className="h-4 w-4" aria-hidden="true" /></span> : (
                                    <input type="checkbox" checked={row.selected} disabled={busy} onChange={(event) => patchRow(note.id, row.id, { selected: event.target.checked })} aria-label={`${row.material || `Zeile ${rowIndex + 1}`} für Übernahme auswählen`} className="mt-2 h-4 w-4 rounded border-slate-300 accent-[var(--bauwerk-teal)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bauwerk-teal" />
                                  )}
                                </td>
                                <td className="px-2 py-2.5 align-top"><Input aria-label="Artikelnummer" value={row.articleNumber} onChange={(event) => patchRow(note.id, row.id, { articleNumber: event.target.value })} disabled={locked} className="h-9 min-w-28 text-xs" /></td>
                                <td className="px-2 py-2.5 align-top"><Input aria-label="Material" value={row.material} onChange={(event) => patchRow(note.id, row.id, { material: event.target.value })} disabled={locked} className={`h-9 min-w-44 text-xs ${!row.material.trim() && row.selected && !row.appliedItemId ? "border-amber-400 bg-amber-50" : ""}`} /></td>
                                <td className="px-2 py-2.5 align-top">{nullableNumber(row.quantity, (quantity) => patchRow(note.id, row.id, { quantity }), "Menge", locked)}</td>
                                <td className="px-2 py-2.5 align-top"><Input aria-label="Einheit" value={row.unit} onChange={(event) => patchRow(note.id, row.id, { unit: event.target.value })} disabled={locked} placeholder="z. B. m²" className={`h-9 min-w-20 text-xs ${!row.unit.trim() && row.selected && !row.appliedItemId ? "border-amber-400 bg-amber-50" : ""}`} /></td>
                                <td className="px-2 py-2.5 align-top"><Input aria-label="Anmerkung" value={row.notes} onChange={(event) => patchRow(note.id, row.id, { notes: event.target.value })} disabled={locked} className="h-9 min-w-36 text-xs" /></td>
                                <td className="px-2 py-2.5 align-top">{nullableNumber(row.unitPrice, (unitPrice) => patchRow(note.id, row.id, { unitPrice }), "Netto-Einheitspreis in Euro", locked)}</td>
                                <td className="px-3 py-3 text-right align-top font-medium tabular-nums text-slate-700">{rowTotal === null ? "—" : money.format(rowTotal)}</td>
                                <td className="px-2 py-2.5 align-top">
                                  {!row.appliedItemId && <Button type="button" variant="ghost" size="icon" aria-label={`Zeile ${rowIndex + 1} entfernen`} disabled={busy} onClick={() => removeRow(note.id, row.id)} className="h-9 w-9 text-slate-500 hover:text-red-700"><Trash2 className="h-4 w-4" aria-hidden="true" /></Button>}
                                </td>
                              </tr>
                            );
                          })}
                          {!note.rows.length && <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-slate-500">Noch keine Materialzeilen. Sie können eine Zeile manuell ergänzen.</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <footer className="flex flex-col gap-3 border-t border-slate-200 bg-slate-50/70 px-3 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-4">
                    <Button type="button" variant="outline" size="sm" onClick={() => addRow(note.id)} disabled={busy || note.rows.length >= 200}>
                      <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Zeile ergänzen
                    </Button>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                      {eligibleRows.length > 0 && incompleteCount > 0 && <p className="text-xs text-amber-800" role="status">{incompleteCount} ausgewählte {incompleteCount === 1 ? "Zeile ist" : "Zeilen sind"} unvollständig: Material, Menge &gt; 0, Einheit und EP eintragen.</p>}
                      <Button type="button" size="sm" onClick={() => onApply(note.id)} disabled={busy || !canApply} className="bg-bauwerk-orange hover:bg-bauwerk-light-orange">
                        <Check className="mr-1.5 h-4 w-4" aria-hidden="true" /> Ausgewählte Positionen übernehmen
                      </Button>
                    </div>
                  </footer>
                  <p className="sr-only">Lieferschein {noteIndex + 1} von {notes.length}</p>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
